import { observer } from "mobx-react-lite";
import React from "react";
import { useEffect, useMemo } from "react";

export type DisplayMode = "image" | "video";

// Use the interface to constrain the generic T
interface GenericGridProps<T extends IGridItem> {
    title: string;
    items: T[];
    isReloading: boolean;
    loadAction: () => Promise<void>;
    setReloadAction: (state: boolean) => void;
    renderItem: (item: T) => JSX.Element;
    // New optional filter prop
    filterPredicate?: (item: T) => boolean; 
}

// Generic Props: 
// S = Store Type, T = Data Model Type
interface Props<T> {
    title: string;
    items: T[];
    isReloading: boolean;
    loadAction: () => Promise<void>;
    setReloadAction: (state: boolean) => void;
    renderItem: (item: T) => JSX.Element;
}

// Define what a "Grid Item" must look like
export interface IGridItem {
    id: string;
    title: string;
    name: string;
    description?: string;
    displayMode?: string;
    categoryVideo?: string;
    categoryImage?: string;
    parentId?:string;    
    // Add any other shared fields here
}

export const GenericGrid = observer(function GenericGrid<T extends IGridItem>({ 
    title, items, isReloading, loadAction, setReloadAction, renderItem,filterPredicate
}: GenericGridProps<T>) {
    useEffect(() => {
        if (isReloading || items.length === 0) {
            loadAction().then(() => setReloadAction(false));
        }
    }, [isReloading, items.length, loadAction, setReloadAction]);    

    const displayedItems = useMemo(() => {
    // 1. Fallback to empty array if items is null/undefined
    const safeItems = items ?? [];

    // 3. Filter only if predicate exists, otherwise return the safe array
    const filtered = filterPredicate 
        ? safeItems.filter((item) => {
            try {
                return filterPredicate(item);
            } catch (e) {
                console.error("Filter predicate failed for item:", item, e);
                return false; // Exclude items that cause predicate errors
            }
        })
        : safeItems;
    return filtered;
}, [items, filterPredicate]);

    return (
        <section data-bs-version="5.1" className="features02 tokenm5 cid-uVpvrJeMSC" style={{marginTop:'110px'}}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 col-lg-10">
                        <div className="title-wrapper">
                            <h1 className="mbr-section-title mbr-fonts-style display-2">
                                <strong>{title}</strong>
                            </h1>
                        </div>
                        <div className="items-wrapper" style={{ gridTemplateColumns: '1fr 1fr' }}>
                            {/* {!isReloading && items.map((item, index) => renderItem(item))} */}
                            {!isReloading && displayedItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    {renderItem(item)}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});